/**
 * Tools to manipulate data maps and records
 * @packageDocumentation
 */

import { getProp, setProp } from "./any.tools";
import { ArrayMap } from "./collection/arrayMap";

/**
 * Get keys from a typescript Map class 
 *
 * @template T - map key
 * @template K - map value
 * @param {Map<T, K>} map - From retrieve <T> keys list
 * @returns {*}  {T[]}
 */
export function getMapKeys<T, K>(map: Map<T, K>): T[] {
    return [...map.keys()];
}

/**
 * Get values in array format from a typescript Map class 
 *
 * @template T - map key
 * @template K - map value
 * @param {Map<T, K>} map - From retrieve <K> values list
 * @returns {*}  {K[]}
 */
export function getMapValues<T, K>(map: Map<T, K>): K[] {
    return [...map.values()];
}

/**
 * record[key].push(value); and creates record[key] if it does not exists
 */
export function addToArrayRecord<T, K extends string | number>(record: Partial<Record<K, T[]>>, key: K, value: T): void {
    let arr = record[key];
    if (arr == null) {
        arr = [];
        record[key] = arr;
    }
    arr.push(value);
}

/**
 * map[key].add(value); and creates map[key] if it does not exists
 */
export function addToSetMap<K, T>(map: Map<K, Set<T>>, key: K, value: T): void {
    let set = map.get(key);
    if (!set) { set = new Set; map.set(key, set); }
    set.add(value);
}

/**
 * Separates a list of objects according to their value on the field named "key"
 * ! adds to map even if key is null or undefined
 * @returns a map of arrays, indexed with all unique values for the field "key", each array containing matching objects
 */
export function buildArrayMap<T, TKey extends keyof T>(objects: T[] | undefined, key: TKey): Map<T[TKey], T[]> {
    return new ArrayMap({ acceptNullKey: true }, { objects, key });
}

/**
 * Separates a list of objects according to the value returned by f
 * If the value returned by f is undefined, the element is not added to the map
 * @returns a map of arrays, indexed with all unique values returned by f, each array containing matching objects
 */
export function buildArrayMapWithFunc<T, K>(
    objectList: T[] | undefined,
    f: (obj: T) => K | undefined,
    p?: {
        acceptNullKey?: boolean
    }
): Map<K, T[]> {
    const objectsToAdd = objectList && f ? { objects: objectList, key: f } : undefined;
    return new ArrayMap({ acceptNullKey: p?.acceptNullKey ?? false }, objectsToAdd);
}

/**
 * Separates a list of objects according to their value on the field named "key" and keep one of each unique key
 * ! adds to map even if key is null or undefined
 * @returns a map of objects indexed with all unique values for the field "key"
 */
export function buildObjectMap<T, TKey extends keyof T>(objectList: T[] | undefined, key: TKey): Map<T[TKey], T> {
    const result = new Map<T[TKey], T>();
    if (objectList != null) {
        for (const o of objectList) {
            result.set(o[key], o);
        }
    }
    return result;
}

/**
 * Separates a list of objects according to the value returned by f and keep one of each unique key
 * If the value returned by f is undefined, the element is not added to the map
 * @returns a map of objects indexed with all unique values for the field "key"
 */
export function buildObjectMapWithFunc<T, K>(objectList: T[] | undefined, f: (obj: T) => K): Map<NonNullable<K>, T> {
    const result = new Map<NonNullable<K>, T>();
    if (objectList != null) {
        for (const o of objectList) {
            const k = f(o);
            if (k != null) {
                result.set(k, o);
            }
        }
    }
    return result;
}

/**
 * Separates a list of objects according to their value on the field named "key" and keep one of each unique key
 * ! key must be a string or a number and for now Typescript is not checking it
 * ! adds to record even if key is null or undefined (reminder: key is a string in the record => "undefined" string)
 * Calls duplicateError if several objects have the same key
 * @returns a record of objects indexed with all unique values for the field "key"
 */
export function buildObjectRecord<T, TKey extends keyof T>(objectList: T[] | undefined, key: TKey, duplicateError?: (key: string | number | undefined, previousItem: T, item: T) => void): Record<string | number, T> {
    const result = {};
    if (objectList != null) {
        for (const o of objectList) {
            const k = o[key] as unknown as string | number;
            if (duplicateError && getProp(result, k) !== undefined) {
                duplicateError(k, getProp(result, k), o);
            }
            setProp(result, k, o);
        }
    }
    return result;
}

/**
 * Separates a list of objects according to the value returned by f
 * If the value returned by f is undefined, the element is not added to the map
 * Calls duplicateError if several objects have the same key
 * @returns a record of objects indexed with all unique values for the field "key"
 */
export function buildObjectRecordWithFunc<T>(objectList: T[] | undefined, f: (obj: T) => string | number | undefined, duplicateError?: (key: string | number | undefined, previousItem: T, item: T) => void): Record<string | number, T> {
    const result = {};
    if (objectList != null) {
        for (const o of objectList) {
            const k = f(o);
            if (k == null) {
                continue;
            }
            if (duplicateError && getProp(result, k) !== undefined) {
                duplicateError(k, getProp(result, k), o);
            }
            setProp(result, k, o);
        }
    }
    return result;
}

/**
 * Separates a list of objects according to their value on the field named "key"
 * ! adds to record even if key is null or undefined (reminder: key is a string in the record => "undefined" string)
 * @returns a record of arrays, indexed with all unique values for the field "key", each array containing matching objects
 */
export function buildArrayRecord<T, TKey extends keyof T>(objectList: T[] | undefined, key: TKey): Record<string | number, T[]> {
    const result = {};
    if (objectList != null) {
        for (const o of objectList) {
            const k = o[key];
            addToArrayRecord(result, k as unknown as string | number, o);
        }
    }
    return result;
}

/**
 * Separates a list of objects according to their value on the field named "key" where key is an array.
 * @example result = buildArrayRecord_keyIsArray([{ "activity": "a1", "agentIds": [1, 2] },
 *                                       { "activity": "a2", "agentIds": [1, 3] },
 *                                       { "activity": "a3", "agentIds": [1, 3] }], "agentIds");
 * // will give =>
 * result = {
 *   1: [{ "activity": "a1", "agentIds": [1, 2] }, { "activity": "a2", "agentIds": [1, 3] }, { "activity": "a3", "agentIds": [1, 3] }],
 *   2: [{ "activity": "a1", "agentIds": [1, 2] }],
 *   3: [{ "activity": "a2", "agentIds": [1, 3] }, { "activity": "a3", "agentIds": [1, 3] }]
 * };
 * @returns a record of arrays, indexed with all unique values for the field "key", each array containing matching objects
 */
export function buildArrayRecord_keyIsArray<T, TKey extends keyof T>(objectList: T[] | undefined, key: TKey): Record<string | number, T[]> {
    const result = {};
    if (objectList != null) {
        for (const o of objectList) {
            const k = o[key];
            if (Array.isArray(k)) {
                for (const kv of k) {
                    addToArrayRecord(result, kv as unknown as string | number, o);
                }
            }
        }
    }
    return result;
}

/**
 * Separates a list of objects according to the value returned by f
 * If the value returned by f is undefined, the element is not added to the map
 * @returns a map of arrays, indexed with all unique values returned by f, each array containing matching objects
 */
export function buildArrayRecordWithFunc<T>(objectList: T[] | undefined, f: (obj: T) => string | number | undefined): Record<string | number, T[]> {
    const result = {};
    if (objectList != null) {
        for (const o of objectList) {
            const k = f(o);
            if (k == null) {
                continue;
            }
            if (Array.isArray(k)) {
                for (const kv of k) {
                    addToArrayRecord(result, kv as unknown as string | number, o);
                }
            } else {
                addToArrayRecord(result, k as unknown as string | number, o);
            }
        }
    }
    return result;
}

/**
 * Separates a list of objects according to the values returned by f ( like {@link buildArrayRecord_keyIsArray} but with un function instead of key)
 * If the value returned by f is undefined, the element is not added to the map
 * @returns a map of arrays, indexed with all unique values returned by f, each array containing matching objects
 */
export function buildArrayRecordWithFuncReturnArray<T>(objectList: T[] | undefined, f: (obj: T) => string[] | number[] | undefined): Record<string | number, T[]> {
    const result = {};
    if (objectList != null) {
        for (const o of objectList) {
            const k = f(o);
            if (k == null) {
                continue;
            }
            if (Array.isArray(k)) {
                for (const kv of k) {
                    addToArrayRecord(result, kv as unknown as string | number, o);
                }
            }
        }
    }
    return result;
}
